/**
 * Country → region/state options for the Step 1 harvest form (UAT #4).
 *
 * ⭐ THE COUNTRY IS NOT COSMETIC — IT SELECTS THE BUSINESS REGISTER AND THE SEARCH REGION.
 * `jurisdiction` drives which register T1's anchor group consults (AU → ABR for-profit /
 * ACNC charity, NZ → NZBN) and, since 15 September 2026, Serper's `gl`. Picking the wrong
 * country does not merely mislabel a cohort: it sends the highest-value check (50 of 178
 * points) to a register that cannot contain the business, so every candidate returns
 * no-data on the anchor.
 *
 * ⚠ `registerSupported` ANSWERS "CAN THIS COUNTRY'S REGISTER ANCHOR ACTUALLY SCORE TODAY?"
 * — NOT "has someone written an adapter". The distinction is the whole history of this
 * flag, and it has now been wrong in BOTH directions:
 *
 *   · Until 15 September 2026 US was `true` because Middesk was the *intended* vendor.
 *     A plan is not a capability, and the one country with no register at all was the
 *     only unsupported one suppressing the warning saying so.
 *   · On 17 September 2026 a real US adapter shipped (OpenSOSData, ADR-114 §8, in
 *     `lambdas/shared/src/register/`) — and US stays `false`, because
 *     `nexa/opensosdata-api-key` DOES NOT EXIST in Secrets Manager (verified against
 *     account 947326094682 that day). **An unprovisioned adapter is not a capability
 *     either.** Flipping the flag on the strength of merged code would repeat the 15
 *     September defect through a narrower door: the portal would stop warning while every
 *     US candidate still reports the 50-point anchor as no-data.
 *
 * ⛔ SO THE FLIP IS OWED, AND IT IS OWED TO A SECRET, NOT TO A COMMIT. When
 * `nexa/opensosdata-api-key` is provisioned, set US to `true` — nothing else needs to
 * change. The same debt is outstanding for AU for-profits and NZ, whose adapters have been
 * complete for weeks while `nexa/abr-api-key` and `nexa/nzbn-api-key` remain absent; those
 * two read `true` because the AU CHARITY path (ACNC) is keyless and genuinely scores today,
 * which is the asymmetry that makes AU's `true` honest and NZ's generous.
 *
 * ⛔⛔ THE HONEYPOT LISTS WERE AUSTRALIAN, AND THAT IS NOW FIXED (15 September 2026).
 * Until then `EXCLUDED_HOSTS` / `AGGREGATOR_FRAGMENTS` in
 * `lambdas/growth/src/collectors/serper.ts` held `gov.au`, `abc.net.au`, `yellowpages`,
 * `truelocal`, `hipages`, `aussieweb` — no general `.gov`, no US/UK/CA directory at all,
 * and (though NZ was live and selectable the whole time) no NZ directory either.
 *
 * That file names those lists "THE PRIMARY HONEYPOT-AVOIDANCE LEVER, not a tidiness
 * filter": ACT's structural defence is that it DISCOVERS businesses rather than harvesting
 * a pre-existing list, and a directory page IS a pre-existing list. So running them against
 * a non-AU query did not merely add noise, it defeated the sourcing design.
 *
 * Measured on a live "Accountants and bookkeepers / Texas, United States" harvest
 * (13 September 2026): 19 candidates kept, of which roughly HALF were directories,
 * aggregators or government — `texas.gov`, `quora.com`, `clutch.co`, `designrush.com`,
 * `upwork.com`, `xero.com`, `relayfi.com`, `1800accountant.com`, `tx.cpa`. Only `yelp.com`
 * and `facebook.com` were dropped. All five countries were re-validated live against real
 * sector/location pairs before the flags below were flipped back on.
 *
 * ⛔ SO `harvestable` IS NOT A CONVENIENCE SWITCH. It records whether the Lambda's
 * honeypot lists actually cover that country, proven by a live run — never by reasoning
 * that "Serper is jurisdiction-agnostic", which is the reasoning that put all five in the
 * dropdown unvalidated in the first place. To add a SIXTH country: run
 * `lambdas/growth/scripts/live-harvest-hygiene-probe.ts` against two real sector/location
 * pairs in it, add what survives to `serper.ts`, and only then add the row here.
 */

export type CountryCode = 'AU' | 'NZ' | 'US' | 'UK' | 'CAN';

export interface CountryDef {
  code: CountryCode;
  label: string;
  /** What the region field is called locally — shown as the field label. */
  regionLabel: string;
  regions: string[];
  /** False when no business-register adapter exists for this country. */
  registerSupported: boolean;
  /**
   * False when the harvest's honeypot-avoidance lists are not valid for this country —
   * see the ⛔⛔ note above. A non-harvestable country is withheld from the selector
   * entirely rather than warned about: a warning would leave a destructive default one
   * click away, and the failure it causes is silent (a plausible-looking candidate list
   * that is half directories) rather than an error the operator would notice.
   *
   * ⚠ Distinct from `registerSupported`, and the two are NOT redundant. Unharvestable is
   * silent and is hidden; register-less is loud and is warned about. A country can be one
   * without the other — UK and CAN are harvestable and register-less today.
   */
  harvestable: boolean;
}

export const COUNTRIES: CountryDef[] = [
  {
    code: 'AU',
    label: 'Australia',
    regionLabel: 'State or territory',
    registerSupported: true,
    harvestable: true,
    regions: [
      'New South Wales', 'Victoria', 'Queensland', 'South Australia',
      'Western Australia', 'Tasmania', 'Northern Territory',
      'Australian Capital Territory',
    ],
  },
  {
    code: 'NZ',
    label: 'New Zealand',
    regionLabel: 'Region',
    registerSupported: true,
    harvestable: true,
    regions: [
      'Auckland', 'Bay of Plenty', 'Canterbury', 'Gisborne', "Hawke's Bay",
      'Manawatū-Whanganui', 'Marlborough', 'Nelson', 'Northland', 'Otago',
      'Southland', 'Taranaki', 'Tasman', 'Waikato', 'Wellington', 'West Coast',
    ],
  },
  {
    code: 'US',
    label: 'United States',
    regionLabel: 'State',
    // ⚠ FALSE DESPITE A REAL ADAPTER NOW EXISTING — see the ⚠ note at the top.
    // OpenSOSData shipped 17 September 2026, but `nexa/opensosdata-api-key` is absent from
    // Secrets Manager, so every US candidate still reports the register check as no-data
    // and the warning must keep showing. Flip this the day the secret is created.
    registerSupported: false,
    harvestable: true,
    regions: [
      'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado',
      'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho',
      'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine',
      'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
      'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey',
      'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
      'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina',
      'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia',
      'Washington', 'West Virginia', 'Wisconsin', 'Wyoming',
    ],
  },
  {
    code: 'UK',
    label: 'United Kingdom',
    regionLabel: 'Nation or region',
    registerSupported: false,
    harvestable: true,
    regions: [
      'England — London', 'England — South East', 'England — South West',
      'England — East of England', 'England — East Midlands',
      'England — West Midlands', 'England — Yorkshire and the Humber',
      'England — North West', 'England — North East',
      'Scotland', 'Wales', 'Northern Ireland',
    ],
  },
  {
    code: 'CAN',
    label: 'Canada',
    regionLabel: 'Province or territory',
    registerSupported: false,
    harvestable: true,
    regions: [
      'Alberta', 'British Columbia', 'Manitoba', 'New Brunswick',
      'Newfoundland and Labrador', 'Northwest Territories', 'Nova Scotia',
      'Nunavut', 'Ontario', 'Prince Edward Island', 'Quebec', 'Saskatchewan',
      'Yukon',
    ],
  },
];

// =============================================================================
// Sub-region granularity — County (US) / Suburb (elsewhere), D-009
// =============================================================================

/**
 * ⭐⭐ WHY A THIRD LEVEL EXISTS AT ALL. A state-level harvest is too coarse to be a
 * targeting decision: "Texas" is 109,874 employer establishments in Harris County alone.
 * D-009 confirmed G1's harvest-location granularity extends to County/Suburb
 * (GBP-aligned), so an operator can aim a 50-record cohort at a place where the sector
 * actually concentrates rather than at a state and hoping.
 *
 * ⛔⛔ THIS IS A STATIC SEED, NOT A QUERY, AND THE DISTINCTION IS LOAD-BEARING. Every
 * figure below is a raw count from the US Census Bureau's County Business Patterns 2022
 * and Nonemployer Statistics 2022 releases, queried against api.census.gov and retrieved
 * 16 September 2026 (GROWTH Build Reference Appendix A, which carries the full method and
 * the data-quality notes). It is a SNAPSHOT of ten states, and it will go stale.
 *
 * It is deliberately not the production mechanism. The live path is
 * `lambdas/growth/src/census.ts`, which queries the same two endpoints at request time so
 * a state outside this pilot set needs no code change — that is Track 5 of the S6 build,
 * and Jeff's stated requirement: county/size-band targeting must be queryable live by a
 * future Growth agent (G7), not fixed to a ten-state snapshot. This seed exists so the
 * FORM has something to offer today, synchronously, without an API round-trip per
 * keystroke; it is a convenience list, never the source of truth for a figure.
 */
export interface CountySeed {
  name: string;
  /** State-county FIPS, e.g. `48-201` for Harris County, Texas. The stable join key. */
  fips: string;
  /** Total employer establishments (CBP 2022). */
  estab: number;
  /** Nonemployer establishments — solopreneurs / one-person businesses (NES 2022). */
  solo: number;
  /** Employer establishments with 1–19 staff. */
  micro: number;
  /** 20–99 staff. */
  small: number;
  /** 100–250 staff. Larger firms are out of scope by design (D-009). */
  medium: number;
}

/**
 * Top 5 counties by total employer establishment count, for the ten pilot states.
 *
 * ⚠ THE TEN STATES WERE CHOSEN ON AN EXPLICIT CRITERION, not by size: "broad SME base, not
 * single-employer-dominated" (D-009). A state absent from this map is not unsupported —
 * it simply has no seed, and the form falls back to state-level granularity for it until
 * the live Census tool answers.
 *
 * ⚠ `solo` LEGITIMATELY EXCEEDS `estab`, AND THAT IS NOT A TRANSCRIPTION ERROR. They count
 * different populations from different Census releases: `estab` is EMPLOYER establishments
 * (CBP), `solo` is NONEMPLOYER establishments (NES). Miami-Dade has 95,916 of the former
 * and 682,105 of the latter. Anything that "corrects" one against the other is wrong.
 */
const US_COUNTIES: Record<string, CountySeed[]> = {
    'Florida': [
      { name: 'Miami-Dade',    fips: '12-086', estab:  95916, solo:  682105, micro:  87061, small:  7318, medium: 1126 },
      { name: 'Broward',       fips: '12-011', estab:  67274, solo:  362334, micro:  60420, small:  5676, medium:  862 },
      { name: 'Palm Beach',    fips: '12-099', estab:  54903, solo:  235387, micro:  49419, small:  4600, medium:  657 },
      { name: 'Orange',        fips: '12-095', estab:  44612, solo:  200301, micro:  38357, small:  5053, medium:  832 },
      { name: 'Hillsborough',  fips: '12-057', estab:  42366, solo:  177080, micro:  36657, small:  4625, medium:  754 },
    ],
    'Texas': [
      { name: 'Harris',        fips: '48-201', estab: 109874, solo:  547487, micro:  91567, small: 14862, medium: 2378 },
      { name: 'Dallas',        fips: '48-113', estab:  70472, solo:  287240, micro:  57560, small: 10262, medium: 1770 },
      { name: 'Tarrant',       fips: '48-439', estab:  46946, solo:  223153, micro:  38999, small:  6624, medium:  959 },
      { name: 'Travis',        fips: '48-453', estab:  41596, solo:  147031, micro:  35227, small:  5235, medium:  772 },
      { name: 'Bexar',         fips: '48-029', estab:  39091, solo:  184893, micro:  32331, small:  5516, medium:  824 },
    ],
    'North Carolina': [
      { name: 'Mecklenburg',   fips: '37-119', estab:  36081, solo:  120860, micro:  30078, small:  4925, medium:  732 },
      { name: 'Wake',          fips: '37-183', estab:  33076, solo:  110635, micro:  27802, small:  4480, medium:  554 },
      { name: 'Guilford',      fips: '37-081', estab:  14342, solo:   48987, micro:  11802, small:  2121, medium:  301 },
      { name: 'Buncombe',      fips: '37-021', estab:   9720, solo:   32299, micro:   8441, small:  1103, medium:  134 },
      { name: 'Forsyth',       fips: '37-067', estab:   9026, solo:   31760, micro:   7473, small:  1311, medium:  166 },
    ],
    'Georgia': [
      { name: 'Fulton',        fips: '13-121', estab:  40717, solo:  138676, micro:  34162, small:  5096, medium:  968 },
      { name: 'Gwinnett',      fips: '13-135', estab:  27222, solo:  141876, micro:  23760, small:  2832, medium:  459 },
      { name: 'Cobb',          fips: '13-067', estab:  22147, solo:   95640, micro:  18872, small:  2694, medium:  395 },
      { name: 'DeKalb',        fips: '13-089', estab:  18329, solo:   98130, micro:  15975, small:  1929, medium:  282 },
      { name: 'Chatham',       fips: '13-051', estab:   8829, solo:   28521, micro:   7370, small:  1242, medium:  166 },
    ],
    'Tennessee': [
      { name: 'Davidson',      fips: '47-037', estab:  21694, solo:   91037, micro:  17458, small:  3434, medium:  574 },
      { name: 'Shelby',        fips: '47-157', estab:  19659, solo:   99308, micro:  15622, small:  3309, medium:  518 },
      { name: 'Knox',          fips: '47-093', estab:  12350, solo:   42713, micro:  10003, small:  2003, medium:  265 },
      { name: 'Hamilton',      fips: '47-065', estab:   9726, solo:   33255, micro:   7961, small:  1471, medium:  208 },
      { name: 'Williamson',    fips: '47-187', estab:   8449, solo:   33658, micro:   7087, small:  1118, medium:  172 },
    ],
    'Utah': [
      { name: 'Salt Lake',     fips: '49-035', estab:  35284, solo:  105745, micro:  29831, small:  4412, medium:  696 },
      { name: 'Utah',          fips: '49-049', estab:  17057, solo:   65639, micro:  14844, small:  1803, medium:  276 },
      { name: 'Davis',         fips: '49-011', estab:   8485, solo:   29830, micro:   7430, small:   914, medium:  111 },
      { name: 'Washington',    fips: '49-053', estab:   7001, solo:   19927, micro:   6243, small:   677, medium:   64 },
      { name: 'Weber',         fips: '49-057', estab:   6327, solo:   18296, micro:   5464, small:   722, medium:  101 },
    ],
    'Arizona': [
      { name: 'Maricopa',      fips: '04-013', estab: 107648, solo:  391119, micro:  90659, small: 13819, medium: 2191 },
      { name: 'Pima',          fips: '04-019', estab:  21083, solo:   78152, micro:  17860, small:  2727, medium:  373 },
      { name: 'Yavapai',       fips: '04-025', estab:   6463, solo:   22889, micro:   5765, small:   637, medium:   41 },
      { name: 'Pinal',         fips: '04-021', estab:   4534, solo:   28232, micro:   3955, small:   500, medium:   47 },
      { name: 'Mohave',        fips: '04-015', estab:   4292, solo:   13725, micro:   3742, small:   501, medium:   40 },
    ],
    'Colorado': [
      { name: 'Denver',        fips: '08-031', estab:  27347, solo:   79105, micro:  23097, small:  3444, medium:  581 },
      { name: 'Arapahoe',      fips: '08-005', estab:  20149, solo:   68238, micro:  17222, small:  2366, medium:  420 },
      { name: 'El Paso',       fips: '08-041', estab:  18769, solo:   62159, micro:  16294, small:  2116, medium:  248 },
      { name: 'Jefferson',     fips: '08-059', estab:  18082, solo:   59952, micro:  16080, small:  1728, medium:  211 },
      { name: 'Boulder',       fips: '08-013', estab:  12985, solo:   40216, micro:  11450, small:  1310, medium:  161 },
    ],
    'South Carolina': [
      { name: 'Charleston',    fips: '45-019', estab:  15484, solo:   45488, micro:  13170, small:  2025, medium:  224 },
      { name: 'Greenville',    fips: '45-045', estab:  15174, solo:   51873, micro:  12737, small:  2027, medium:  283 },
      { name: 'Horry',         fips: '45-051', estab:  10313, solo:   34595, micro:   8908, small:  1269, medium:  115 },
      { name: 'Richland',      fips: '45-079', estab:   9402, solo:   34281, micro:   7874, small:  1288, medium:  172 },
      { name: 'Lexington',     fips: '45-063', estab:   7279, solo:   23748, micro:   6169, small:   941, medium:  123 },
    ],
    'Idaho': [
      { name: 'Ada',           fips: '16-001', estab:  16806, solo:   48656, micro:  14460, small:  2018, medium:  246 },
      { name: 'Kootenai',      fips: '16-055', estab:   6069, solo:   16427, micro:   5364, small:   621, medium:   65 },
      { name: 'Canyon',        fips: '16-027', estab:   5820, solo:   18268, micro:   5114, small:   602, medium:   79 },
      { name: 'Bonneville',    fips: '16-019', estab:   4074, solo:   10783, micro:   3452, small:   542, medium:   60 },
      { name: 'Twin Falls',    fips: '16-083', estab:   3000, solo:    6981, micro:   2602, small:   358, medium:   30 },
    ],
};

/**
 * What the third level is called in this country.
 *
 * ⚠ ONLY THE US HAS SEED DATA TODAY. The other four return a label and an empty list, so
 * the form degrades to state-level granularity rather than showing an empty dropdown. The
 * labels are stated now because they are a naming decision, not a data one — an AU harvest
 * narrows by SUBURB, not by county, and calling it "County" in the AU form would be wrong
 * on the day someone seeds it.
 */
export const subRegionLabel = (code: CountryCode): string =>
  code === 'US' ? 'County' : code === 'UK' ? 'District' : code === 'CAN' ? 'Municipality' : 'Suburb';

/**
 * The sub-regions offerable for a given country + region, best-first by establishment count.
 *
 * Returns `[]` when there is no seed — the caller must treat that as "offer state-level
 * only", never as "this region has no businesses".
 */
export function subRegionsFor(code: CountryCode, region: string): CountySeed[] {
  if (code !== 'US') return [];
  return US_COUNTIES[region] ?? [];
}

/** True when this country + region can be narrowed further. Drives whether the field shows. */
export const hasSubRegions = (code: CountryCode, region: string): boolean =>
  subRegionsFor(code, region).length > 0;

/** Every state carrying seed data — exported so a test can assert the pilot set is intact. */
export const SEEDED_US_STATES: string[] = Object.keys(US_COUNTIES);

export const countryDef = (code: CountryCode): CountryDef =>
  COUNTRIES.find((c) => c.code === code) ?? COUNTRIES[0];

/**
 * The countries the selector actually offers. Derived, never hand-listed — a second
 * literal list would be free to disagree with the `harvestable` flags above, and the
 * whole point of the flag is that one place decides.
 *
 * All five as of 15 September 2026, each validated by a live harvest run. The derivation
 * stays because the flag is what a future sixth country has to earn.
 */
export const HARVESTABLE_COUNTRIES: CountryDef[] = COUNTRIES.filter((c) => c.harvestable);

/**
 * The server's cap on `targetCount`, mirrored so the form can refuse a value the API
 * would reject anyway.
 *
 * ⚠ THIS IS A MIRROR, AND A MIRROR IS ALLOWED TO DRIFT — so a test asserts it against
 * `MAX_TARGET_COUNT` in `lambdas/growth/src/handlers.ts` by reading that source. The
 * client copy is a courtesy that keeps the operator out of a pointless round-trip; the
 * server's is the one that holds, and a form-level check is never a substitute for it
 * (the previous `max={50}` attribute constrained the spinner arrows and nothing else).
 */
export const MAX_TARGET_COUNT = 50;

/**
 * The `location` string sent to the harvest.
 *
 * Serper takes a free-form location, and a region alone is ambiguous across countries
 * (there is a Victoria in both AU and CAN; a Washington state and a Washington DC). So the
 * country name is appended — except when the operator free-typed a location, which is
 * taken verbatim on the assumption they know what they meant.
 *
 * ⛔ THAT ASSUMPTION IS NOT LOAD-BEARING, AND IT USED TO BE. A free-typed "Texas, United
 * States" left `jurisdiction` on its AU default and nothing anywhere noticed. The API now
 * cross-checks the two and refuses the contradiction with
 * `location_jurisdiction_mismatch` — see `lambdas/growth/src/location-jurisdiction.ts`.
 * Appending the country to free text instead was considered and rejected: it would have
 * turned an operator error into a nonsense query ("Texas, Australia") that Serper answers
 * happily, converting a detectable fault into an undetectable one.
 */
export function composeLocation(
  region: string,
  code: CountryCode,
  freeTyped: boolean,
  subRegion = '',
): string {
  const r = region.trim();
  if (!r) return '';
  if (freeTyped) return r;

  /**
   * ⭐ THE SUB-REGION IS PREPENDED, AND ITS LOCAL NOUN GOES WITH IT. Serper takes a
   * free-form location, and "Harris, Texas, United States" is meaningfully worse than
   * "Harris County, Texas, United States" — the bare name is a surname, a street and a
   * dozen towns, while the qualified form is one place. The noun comes from
   * `subRegionLabel` so an AU suburb never gets called a county.
   *
   * ⚠ THE STATE IS STILL SENT SEPARATELY as the harvest request's `region` field, and that
   * is not redundancy. Once a county is in this string it can no longer be safely parsed
   * back out: Utah has a Washington county, so "Washington County, Utah, United States"
   * names two states to any text matcher. `location` is for the SEARCH; `region` is for
   * the REGISTER. See `lambdas/growth/src/cohort-store.ts`'s `region` field.
   */
  const sub = subRegion.trim();
  if (!sub) return `${r}, ${countryDef(code).label}`;
  return `${sub} ${subRegionLabel(code)}, ${r}, ${countryDef(code).label}`;
}

/**
 * The jurisdiction the API expects.
 *
 * ⚠ Only AU and NZ are register-backed in the scoring engine. US, UK and CAN are mapped to
 * their own codes and the API's schema accepts them, but the register collector has no
 * adapter — so they degrade to no-data rather than silently being scored as though they
 * were somewhere else. Mapping UK → 'US' to "make it work" would be far worse: it would
 * query the wrong national register and could return a FALSE MATCH on a same-named company.
 *
 * ⚠ These codes are OURS, not Serper's. The Lambda maps them to `gl` on the way out, where
 * the United Kingdom becomes `gb` — see `glFor` in `lambdas/growth/src/collectors/serper.ts`.
 */
export const jurisdictionFor = (code: CountryCode): string => code;
